const moment = require('moment');
const countryList = require('country-list');
const json2csv = require('json2csv');
const { formatDistanceToNowStrict } = require('date-fns');
const compareVersionsModule = require('compare-versions');
const { getRiskIndicatorByRiskRule, getSyslogValueByRiskRule } = require('./insight');

const {
    RISK_TO_NAME,
    SAN_EXTENSION_ID,
    LOGS_DISPLAY_ACCOUNT_TYPES,
    AUTHENTICATION_LOG_REASONS,
    DENY_REASONS,
    ALLOW_REASONS,
    AUTHENTICATOR_TYPES,
    RETROSPECTIVE_ANSWER,
    WEAK_ENCRYPTION_TYPES,
    OBSOLETE_TLS_TYPES,
    WEAK_CIPHER_TYPES,
    DIRECTORY_TYPES,
    FEATURE_FLAG_SEVERITIES,
    AUTHENTICATION_EXPORT_FIELDS,
    ES_LOGS_GROUP_BY,
    AD_CONNECT_STATUSES,
    AUTH_MODE_FILTERS_OPTIONS,
    BASELINE_CHANGE,
    RULES_TYPES,
    ACCOUNT_MATCHING_TYPES
} = require('./consts');
const { unset, get, isPlainObject, transform } = require('lodash');
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[$&+,:;=?@#|'<>.^*()%!-])(?=.*[0-9])(?=.*[a-z]).{8,}$/;
const VERSION_REGEX = /\d+\.\d+\.\d+\.\d+/;
const CERT_FQDN_REGEX = /Subject:.*?CN\s*=\s*([^,\n]*)/i;
const CERT_EXPIRE_REGEX = /Not After\s*:\s*(.*)/i;
const CERT_SAN_REGEX = /X509v3 Subject Alternative Name:\s*([^\n]*)/i;
const CERT_ALGO_REGEX = /Signature Algorithm:\s*([^\n]*)/i;

let logger = console;

const setLogger = (newLogger) => {
    logger = newLogger;
};

const toFirstLetterUppercase = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

const entitiesToShortListFormat = (items) => {
    if (!items || items.length < 1) {
        return '';
    }
    //show domain if we have 2 entities with the same name
    const showDomain = new Set(items.map((item) => item.name)).size !== items.length;
    const entitiesInShortListFormat = [];
    for (const item of items) {
        if (item.address && item.mask) {
            entitiesInShortListFormat.push({
                displayText: `${item.address}${item.mask === '32' ? '' : '/' + item.mask}`,
                title: null
            });
        } else {
            const pathText = item.path ? ` ${item.path}` : '';
            const domainText = item.domain_name ? ` ${item.domain_name}` : '';
            const domainTextWithPar = item.domain_name ? ` (${item.domain_name})` : '';
            const deletedText = item.is_deleted ? ` (deleted)` : '';
            const disabledText = item.is_disabled ? ' (disabled)' : '';
            entitiesInShortListFormat.push({
                displayText: `${item.name}${showDomain ? domainTextWithPar : ''}${deletedText + disabledText}`,
                title: pathText + domainText + deletedText + disabledText
            });
        }
    }
    return entitiesInShortListFormat;
};

const splitAndTransformEntities = (entityList, entitiesTransformer) => {
    if (!entityList) {
        entityList = [];
    }
    const selectedEntities = [];
    const excludedEntities = [];
    for (const entity of entityList) {
        if (entity.excluded) {
            excludedEntities.push(entity);
        } else {
            selectedEntities.push(entity);
        }
    }
    return {
        selectedEntities: entitiesTransformer(entitiesToShortListFormat(selectedEntities)),
        excludedEntities: entitiesTransformer(entitiesToShortListFormat(excludedEntities))
    };
};

const unixToDate = function (unix) {
    if (!unix) {
        return '';
    }

    return moment.unix(unix / 1000).format('dddd, MMMM Do YYYY');
};

const validateAdminConsolePass = function (oldPass, newPass, passwordText) {
    if (!newPass || newPass.length < 8) {
        return { results: false, error: `${passwordText} must contain at least 8 characters` };
    } else if (!PASSWORD_REGEX.test(newPass)) {
        return {
            results: false,
            error: `${passwordText} must include an uppercase letter, lowercase letter, number and non-alphanumeric character`
        };
    } else if (newPass === oldPass) {
        return { results: false, error: `${passwordText} must be different than old password` };
    }
    return { results: true, error: '' };
};

const extractDataFromCert = (cert, isEliptic = false) => {
    let fqdn = '';
    let san = '';
    let expire = '';
    const siginfo = {
        algorithmOid: ''
    };

    try {
        if (typeof cert === 'string' || isEliptic) {
            // If cert is an OpenSSL output, parse it as text
            logger.debug('Parsing OpenSSL certificate output');
            logger.debug(`Certificate output length: ${cert.length} characters`);
            logger.debug(`Certificate output preview: ${cert.substring(0, 200)}`);

            const fqdnMatch = cert.match(CERT_FQDN_REGEX);
            if (fqdnMatch) {
                fqdn = fqdnMatch[1].trim();
                logger.debug(`Found FQDN: ${fqdn}`);
            } else {
                logger.debug('Could not find FQDN in certificate');
            }

            const expireMatch = cert.match(CERT_EXPIRE_REGEX);
            if (expireMatch) {
                const expireDate = expireMatch[1].trim();
                logger.debug(`Found expiration date: ${expireDate}`);

                const parsedDate = moment(expireDate, [
                    'MMM D HH:mm:ss YYYY [GMT]',
                    'MMM D YYYY HH:mm:ss',
                    'MMM D HH:mm:ss YYYY Z'
                ]);
                if (parsedDate.isValid()) {
                    expire = unixToDate(parsedDate.unix() * 1000);
                    logger.debug(`Parsed expiration date: ${expire}`);
                } else {
                    logger.error(`Failed to parse expiration date: ${expireDate}`);
                }
            } else {
                logger.debug('Could not find expiration date in certificate');
            }

            const sanMatch = cert.match(CERT_SAN_REGEX);
            if (sanMatch) {
                const sanText = sanMatch[1].trim();
                logger.debug(`Found SAN: ${sanText}`);
                san = sanText
                    .split(', ')
                    .map((s) => s.trim())
                    .join('\n');
                logger.debug(`Processed SAN: ${san}`);
            } else {
                logger.debug('Could not find SAN in certificate');
            }

            const algoMatch = cert.match(CERT_ALGO_REGEX);
            if (algoMatch) {
                const algoText = algoMatch[1].trim();
                logger.debug(`Found signature algorithm: ${algoText}`);
                siginfo.algorithmOid = algoText;
            } else {
                logger.debug('Could not find signature algorithm in certificate');
                siginfo.algorithmOid = 'sha256WithRSAEncryption';
                logger.debug('Using default signature algorithm: sha256WithRSAEncryption');
            }
        } else {
            logger.debug('Parsing node-forge certificate object');
            const sans = [];
            if (cert.extensions) {
                const san_extension = cert.extensions.find((extension) => extension.id === SAN_EXTENSION_ID);
                if (san_extension && san_extension.altNames) {
                    for (const altName of san_extension.altNames) {
                        sans.push(altName.value);
                    }
                }
            }

            if (cert.validity && cert.validity.notAfter) {
                const expireFromCert = moment(cert.validity.notAfter);
                expire = unixToDate(expireFromCert.unix() * 1000);
                logger.debug(`Extracted expiration date from node-forge object: ${expire}`);
            } else {
                logger.warn('Certificate validity or expiration date is missing');
            }

            fqdn = cert.subject?.getField('CN')?.value || '';
            san = sans.join('\n');
            logger.debug(`Extracted FQDN from node-forge object: ${fqdn}`);
            logger.debug(`Extracted SAN from node-forge object: ${san}`);

            if (cert.signatureAlgorithm && cert.signatureAlgorithm.oid) {
                siginfo.algorithmOid = cert.signatureAlgorithm.oid;
                logger.debug(`Extracted signature algorithm from node-forge object: ${siginfo.algorithmOid}`);
            } else {
                siginfo.algorithmOid = 'sha256WithRSAEncryption';
                logger.debug('Using default signature algorithm: sha256WithRSAEncryption');
            }
        }
    } catch (err) {
        logger.error('Error extracting certificate data:', err);
    }

    return { fqdn, san, expire, siginfo };
};

const normalizeRisk = (riskLevel) => {
    if (!riskLevel || riskLevel < 1 || riskLevel > 4) return 4;
    return riskLevel - 1;
};

const riskLevelToText = (riskLevel) => {
    return RISK_TO_NAME[normalizeRisk(riskLevel)];
};

const formatRuleName = (ruleID, ruleType, rulesNames = {}, groupPolicyId, groupPolicies) => {
    try {
        let result = '';
        if (ruleID && ruleType) {
            const ruleIdWithPrefix = getRuleIdWithPrefix(ruleID, ruleType);
            if (groupPolicyId) {
                result = groupPolicies[groupPolicyId]?.name;
            } else if (ruleType === RULES_TYPES.SERVICE_ACCOUNTS || ruleType === RULES_TYPES.FENCING) {
                result = 'Virtual Fencing';
            } else {
                result = `(#${ruleID})`;
                if (rulesNames[ruleIdWithPrefix]) {
                    result = `${rulesNames[ruleIdWithPrefix].name} (#${ruleID})`;
                }
            }

            if (rulesNames[ruleIdWithPrefix] && rulesNames[ruleIdWithPrefix].is_deleted) {
                result = `${result} (deleted)`;
            }
        }

        return result;
    } catch (err) {
        logger.error(err);
        return '';
    }
};

const formatUsername = (logRow, displayAccountType) => {
    try {
        if (displayAccountType === LOGS_DISPLAY_ACCOUNT_TYPES.SAM.value) {
            return logRow.sam || formatUserIdentifier(logRow);
        } else if (displayAccountType === LOGS_DISPLAY_ACCOUNT_TYPES.UPN.value) {
            return logRow.upn || formatUserIdentifier(logRow);
        }

        const userIdentifier = logRow.upn || formatUserIdentifier(logRow);
        return userIdentifier;
    } catch (err) {
        logger.error(err);
        return '';
    }
};

const formatUserIdentifier = (logRow) => {
    return logRow.user_identifier_display_name || logRow.user_identifier || 'n/a';
};

const formatUserResponse = (mfaResponse, ruleAction) => {
    const defaultResponse = 'n/a';

    try {
        if (!ruleAction || !mfaResponse) {
            return [defaultResponse];
        }

        if (typeof mfaResponse === 'boolean') {
            mfaResponse = mfaResponse.toString();
        }

        const mfaResponseConfigKey = Object.keys(AUTHENTICATION_LOG_REASONS).find(
            (reason) => AUTHENTICATION_LOG_REASONS[reason].USER_RESPONSE === mfaResponse.toLowerCase()
        );
        if (mfaResponseConfigKey) {
            const displayText = AUTHENTICATION_LOG_REASONS[mfaResponseConfigKey].DISPLAY;
            const tooltipText = AUTHENTICATION_LOG_REASONS[mfaResponseConfigKey].DESC;
            return [displayText, tooltipText];
        }
        return [defaultResponse];
    } catch (err) {
        logger.error(err);
        return defaultResponse;
    }
};

const formatResult = (result, reason, forExport, errorCode = '') => {
    try {
        if (result === true || result === 'true') {
            if (forExport) {
                if (reason && ALLOW_REASONS[reason]) {
                    return `Allowed (${ALLOW_REASONS[reason]})`;
                }
                return 'Allowed';
            }
            const returnValue = ['greenColumn', 'Allowed'];
            if (reason && ALLOW_REASONS[reason]) {
                returnValue.push(ALLOW_REASONS[reason]);
            }
            return returnValue;
        } else if (result === false || result === '') {
            if (forExport) {
                if (reason && DENY_REASONS[reason]) {
                    if (errorCode) {
                        return `Denied (${DENY_REASONS[reason]} ${errorCode})`;
                    }
                    return `Denied (${DENY_REASONS[reason]})`;
                }
                return 'Denied';
            }
            const returnValue = ['redColumn', 'Denied'];
            if (reason && DENY_REASONS[reason]) {
                if (errorCode) {
                    returnValue.push(`${DENY_REASONS[reason]} (${errorCode})`);
                } else {
                    returnValue.push(DENY_REASONS[reason]);
                }
            }
            return returnValue;
        }
        if (forExport) {
            return 'n/a';
        }
        return ['', 'n/a'];
    } catch (err) {
        logger.error(err);
        return '';
    }
};

const formatMFATime = (timeToFormat) => {
    try {
        if (!timeToFormat || isNaN(timeToFormat)) {
            return '';
        }

        if (timeToFormat < 1) {
            return '';
        }
        return Math.floor(timeToFormat) + 's';
    } catch (err) {
        logger.error(err);
        return '';
    }
};

const authenticatorTypeToLabel = new Map(Object.values(AUTHENTICATOR_TYPES).map((at) => [at.value, at.label]));

const formatAuthenticator = (authenticator) => {
    if (!authenticator) {
        return '';
    }

    let formattedAuthenticator = authenticator;
    if (authenticatorTypeToLabel.has(authenticator)) {
        formattedAuthenticator = authenticatorTypeToLabel.get(authenticator);
    }

    return formattedAuthenticator;
};

const formatDestination = (server, serverIp, protocol, directoryType, defaultDestinations) => {
    try {
        const lowerProtocol = protocol.toLowerCase();
        const isIpDestination = lowerProtocol === 'ldap' || lowerProtocol === 'ldaps' || lowerProtocol === 'radius';
        const destinationKey = isIpDestination ? serverIp : server;

        if (destinationKey && destinationKey.toLowerCase() !== 'n/a') {
            return [destinationKey, false];
        }

        for (const destKey in defaultDestinations) {
            const lowerDestKey = destKey.toLocaleLowerCase();
            if (directoryType.toLowerCase() === lowerDestKey) {
                return [defaultDestinations[destKey], true];
            }
            if (lowerProtocol === lowerDestKey) {
                return [defaultDestinations[destKey], true];
            }
        }
    } catch (err) {
        logger.error(
            `Failed to formatDestination with data: server:${server}, serverIp:${serverIp}, protocol:${protocol}, directoryType:${directoryType}. Error: ${err}`
        );
    }
    return [null, true];
};

const formatDate = (dateToFormat, navigator = null, showMillis = true, timeZoneFormat = false) => {
    try {
        if (!dateToFormat) {
            return '';
        }

        if (navigator) {
            try {
                const locale = navigator.languages ? navigator.languages : navigator.language;
                moment.locale(locale);
            } catch (err) {}
        }
        if (timeZoneFormat) {
            return moment(dateToFormat).local().format();
        }
        const format = showMillis ? 'L HH:mm:ss.SSS' : 'L HH:mm:ss';
        return moment(dateToFormat).local().format(format);
    } catch (err) {
        logger.error(err);
        return '';
    }
};

const formatService = (protocol, serviceName, krbRenewal) => {
    const protocolNameLower = protocol ? protocol.toLowerCase() : '';
    const serviceNameLower = serviceName ? serviceName.toLowerCase() : '';

    if (protocolNameLower === 'desktop' && serviceNameLower.includes('_rdp')) {
        const splittedService = serviceName.split('_');
        return `${splittedService[0]} (${splittedService[1]})`;
    }

    if (serviceNameLower === 'n/a') {
        return '';
    } else if (serviceNameLower === 'krbtgt' && krbRenewal) {
        return `${serviceName} - renewal`;
    }

    return serviceName;
};

const getWasItYouDisplayNameByLog = (logRow) => {
    const enumVal = Object.values(RETROSPECTIVE_ANSWER).find(
        (enumValue) => enumValue.key === logRow.retrospective_answer
    );
    return enumVal ? enumVal.displayName : '';
};

const getRiskIndicatorsForSyslog = (logRiskRules) => {
    const riskIndicators = new Set();
    if (logRiskRules && logRiskRules.length > 0) {
        logRiskRules.forEach((riskRule) => {
            const syslogValue = getSyslogValueByRiskRule(riskRule);
            riskIndicators.add(syslogValue);
        });
    }
    return Array.from(riskIndicators);
};

const getRiskIndicatorsTitles = (logRiskRules) => {
    const riskIndicators = new Set();
    if (logRiskRules && logRiskRules.length > 0) {
        logRiskRules.forEach((riskRule) => {
            const riskIndicator = getRiskIndicatorByRiskRule(riskRule);
            if (riskIndicator) {
                riskIndicators.add(riskIndicator.authTitle);
            }
        });
    }
    return Array.from(riskIndicators);
};

const getExtraRiskIndicatorDescription = (logData, extraDetailsType, extraDetailsField) => {
    const value = get(logData, extraDetailsField, '');

    switch (extraDetailsType) {
        case 'encryption_type':
            return WEAK_ENCRYPTION_TYPES[value];
        case 'tls_type':
            return OBSOLETE_TLS_TYPES[value];
        case 'cipher_type':
            return WEAK_CIPHER_TYPES[value];
        default:
            return value;
    }
};

const getLocationText = (logData, countriesList) => {
    if (!logData.geo_device_country) {
        return '';
    }
    const countryCode = logData.geo_device_country.toLowerCase();
    return countriesList[countryCode] || '';
};

const shortenTokenID = (tokenId) => {
    if (!tokenId) {
        return '';
    }

    return tokenId.substring(tokenId.length - 8, tokenId.length);
};

const formatRuleAction = (ruleAction, effectiveRuleID, forExport, protocol) => {
    try {
        if (ruleAction && (ruleAction.toLowerCase() === 'notify' || ruleAction.toLowerCase() === 'alert')) {
            if (forExport) {
                return 'Notify';
            }
            return ['blueColumn', 'Notify'];
        }

        if (
            !ruleAction ||
            ((effectiveRuleID === null || effectiveRuleID <= 0) && protocol && protocol.toLowerCase() !== 'api')
        ) {
            return '';
        }

        if (ruleAction && ruleAction.toLowerCase() === 'mfa') {
            if (forExport) {
                return 'MFA';
            }
            return ['blueColumn', 'MFA'];
        } else if (ruleAction && ruleAction.toLowerCase() === 'allow') {
            if (forExport) {
                return 'Allow';
            }
            return ['greenColumn', 'Allow'];
        } else if (ruleAction && ruleAction.toLowerCase() === 'deny') {
            if (forExport) {
                return 'Deny';
            }
            return ['redColumn', 'Deny'];
        }
    } catch (err) {
        logger.error(err);
        return '';
    }
};

const getFormattedGroupByResultRow = (groupByKey, groupByResult, logsDisplayAccountType, defaultDestinations) => {
    let label = '';
    switch (groupByKey) {
        case ES_LOGS_GROUP_BY.user.key:
            label = formatUsername(groupByResult, logsDisplayAccountType);
            break;
        case ES_LOGS_GROUP_BY.source.key:
            label = groupByResult.host_name || groupByResult.ip_address;
            break;
        case ES_LOGS_GROUP_BY.destination.key:
            label =
                formatDestination(
                    groupByResult.server,
                    groupByResult.server_ip,
                    groupByResult.protocol_type,
                    groupByResult.directory_type,
                    defaultDestinations
                )[0] || 'n/a';
            break;
        default:
            break;
    }
    return {
        label,
        count: groupByResult.count
    };
};

const getFormattedLog = (
    logRow,
    rulesNames,
    logsDisplayAccountType,
    defaultDestinations,
    format_timestamp = true,
    forSyslog = false,
    groupPoliciesNames
) => {
    const [user_response] = formatUserResponse(logRow.user_response, logRow.effective_rule_action);
    return {
        time_stamp: format_timestamp ? formatDate(logRow.time_stamp || logRow.timestamp) : logRow.timestamp,
        time_stamp_tz: formatDate(logRow.time_stamp || logRow.timestamp, null, true, true),
        request_id: logRow.request_id,
        user_identifier: formatUsername(logRow, logsDisplayAccountType),
        user_domain: logRow.user_domain ? logRow.user_domain.toLowerCase() : logRow.user_domain,
        host_name: logRow.host_name,
        ip_address: logRow.ip_address,
        service: formatService(logRow.protocol_type, logRow.service, logRow.krb_renewal),
        server: logRow.server,
        destination:
            formatDestination(
                logRow.server,
                logRow.server_ip,
                logRow.protocol_type,
                logRow.directory_type,
                defaultDestinations
            )[0] || 'n/a',
        server_domain: logRow.server_domain ? logRow.server_domain.toLowerCase() : logRow.server_domain,
        effective_rule_action: formatRuleAction(logRow.effective_rule_action, logRow.effective_rule_id, true),
        effective_rule_id: formatRuleName(
            logRow.effective_rule_id,
            logRow.effective_rule_type,
            rulesNames || {},
            logRow.group_policy_id,
            groupPoliciesNames || {}
        ),
        effective_rule_type: logRow.effective_rule_type,
        user_response,
        user_response_interval: formatMFATime(logRow.user_response_interval),
        risk: riskLevelToText(logRow.risk_level),
        protocol_type: logRow.protocol_type,
        alert: logRow.alert ? 'Yes' : 'No',
        wall_id: logRow.wall_id,
        domain_controller: logRow.domain_controller ? logRow.domain_controller.toUpperCase() : logRow.domain_controller,
        packet_released: logRow.packet_released ? 'True' : 'False',
        action: formatResult(logRow.action, logRow.reason, true, logRow.error_code),
        directory_type:
            logRow.directory_type && DIRECTORY_TYPES[logRow.directory_type]
                ? DIRECTORY_TYPES[logRow.directory_type].displayName
                : DIRECTORY_TYPES.ad.displayName,
        tenant_id: logRow.tenant_id ? logRow.tenant_id : '',
        token_id: logRow.token_id ? logRow.token_id : '',
        authenticator: formatAuthenticator(logRow.authenticator) || 'n/a',
        riskIndicators: forSyslog
            ? getRiskIndicatorsForSyslog(logRow.risk_indicators_v2).join()
            : getRiskIndicatorsTitles(logRow.risk_indicators_v2),
        wasItYou: getWasItYouDisplayNameByLog(logRow),
        mode: getEnumLabelByValue(AUTH_MODE_FILTERS_OPTIONS, logRow.auth_mode),
        country: logRow.geo_device_country ? countryList.getName(logRow.geo_device_country.toLowerCase()) : 'n/a',
        city: logRow.geo_device_city || 'n/a',
        coordinates: getCoordinatesString(logRow)
    };
};

const getCoordinatesString = (log) => {
    let coord = 'n/a';
    if (log.geo_device_latitude && log.geo_device_longitude) {
        coord = `${log.geo_device_latitude}, ${log.geo_device_longitude}`;

        if (log.geo_device_altitude) coord = `${coord}, ${log.geo_device_altitude}`;
    }
    return coord;
};

const getGroupByLogsForExport = (
    groupByKey,
    groupByResult,
    selectedFiltersLabels,
    logsDisplayAccountType,
    defaultDestinations
) => {
    const isWithFilter = !!Object.keys(selectedFiltersLabels).length;
    const filtersHeader = 'Current filters:\n';
    const groupByHeader = `Grouped by,${toFirstLetterUppercase(groupByKey)}\n`;
    let csvData = isWithFilter ? filtersHeader : groupByHeader;
    if (isWithFilter) {
        for (const filterKey in selectedFiltersLabels) {
            csvData += `${selectedFiltersLabels[filterKey].label},${selectedFiltersLabels[filterKey].description}\n`;
        }
        csvData += '\n';
        csvData += groupByHeader;
    }

    csvData += '\n';
    csvData += `${[toFirstLetterUppercase(groupByKey)]},# of authentications\n`;
    for (const resultRow of groupByResult) {
        const { label, count = 0 } = getFormattedGroupByResultRow(
            groupByKey,
            resultRow,
            logsDisplayAccountType,
            defaultDestinations
        );
        if (label) {
            csvData += `${label},${[count]}\n`;
        }
    }

    return csvData;
};

const getLogsForExport = (logs, rulesNames, logsDisplayAccountType, defaultDestinations, groupPoliciesNames) => {
    const exportLogs = [];
    for (const logRow of logs) {
        if (logRow && Object.keys(logRow).length === 1) {
            exportLogs.push(logRow);
        } else {
            exportLogs.push(
                getFormattedLog(
                    logRow,
                    rulesNames,
                    logsDisplayAccountType,
                    defaultDestinations,
                    true,
                    false,
                    groupPoliciesNames
                )
            );
        }
    }

    return json2csv.parse(exportLogs, { fields: AUTHENTICATION_EXPORT_FIELDS });
};

const isFeatureActive = (feature) => {
    return feature === FEATURE_FLAG_SEVERITIES.ACTIVE;
};

const isFeaturePassive = (feature) => {
    return feature === FEATURE_FLAG_SEVERITIES.PASSIVE;
};

const isFeatureDisabled = (feature) => {
    return feature === FEATURE_FLAG_SEVERITIES.DISABLED;
};

const isRulesSyncedInNode = (nodeVersions, currentVersions, isDuringPolicyVersionChange) => {
    let isPoliciesSynced = false;
    let isAdSynced = false;
    if (isDuringPolicyVersionChange || !currentVersions) {
        return { isPoliciesSynced, isAdSynced };
    }
    isPoliciesSynced =
        nodeVersions &&
        nodeVersions.rules === currentVersions.Rules &&
        nodeVersions.memberships_rules === currentVersions.Rules &&
        // last_memberships_timestamp is sent only from Walls >=v5.3.x
        (!nodeVersions.last_memberships_timestamp ||
            nodeVersions.last_memberships_timestamp === currentVersions.CitadelVersions.lastMembershipsTimestamp);

    isAdSynced = nodeVersions && nodeVersions.identifiers === currentVersions.CitadelVersions.identifiers;

    return { isPoliciesSynced, isAdSynced };
};

const isEmptyObject = (objectToCheck) => {
    // does not check that the params is an object
    for (const i in objectToCheck) return false;
    return true;
};

const getEnumPropByValue = (enumValues, value, prop) => {
    const enumValue = Object.values(enumValues).find((enumValue) => enumValue.value === value);
    return enumValue ? enumValue[prop] : undefined;
};
const getEnumValueByLabel = (enumValues, label) => {
    return Object.values(enumValues).find((value) => value.label === label);
};

const getEnumLabelByValue = (enumValues, value) => {
    const enumVal = Object.values(enumValues).find((enumValue) => enumValue.value === value);
    return enumVal ? enumVal.label : undefined;
};

const getEnumEntryByValue = (enumToCheck, value) => {
    return Object.values(enumToCheck).find((entry) => entry.value === value);
};

const getEnumEntryByPropValue = (enumToCheck, propKey, propVal) => {
    return Object.values(enumToCheck).find((entry) => entry[propKey] === propVal);
};

const getBodyFromElasticBulkResponseItem = (esBulkResponseItem) => {
    // each item has a single key that holds it's body
    // we don't have a way of knowing what it is because it changes depending on the input
    const key = Object.keys(esBulkResponseItem)[0];
    return esBulkResponseItem[key];
};

const esBulkWithErrorThrowing = async (esClient, bulk, options = null, errorTypesToIgnore = new Set()) => {
    let body;
    if (options) {
        body = await esClient.bulk(bulk, options);
    } else {
        body = await esClient.bulk(bulk);
    }
    if (body?.errors) {
        const errors = new Set();
        body.items.forEach((item) => {
            const itemBody = getBodyFromElasticBulkResponseItem(item);
            if (itemBody?.error) {
                const { type, reason } = itemBody.error;
                if (errorTypesToIgnore.has(type)) {
                    return;
                }
                if (type && reason) {
                    errors.add(`${type} error that was caused by ${reason}`);
                } else {
                    errors.add('Unknown error: ' + JSON.stringify(itemBody.error));
                }
            } else {
                errors.add('Unknown failure with status code ' + itemBody.status);
            }
        });
        if (errors.size > 0) {
            errors.forEach((errorStr) => logger.error('Elastic Bulk error: ' + errorStr));
            throw 'Failed to run bulk operation on elastic';
        }
    }
};

const getMainEntityDisplay = (mainEntities) => {
    if (!mainEntities?.length) return 'N/A';
    else if (mainEntities.length > 1) return `Multiple Entities (${mainEntities.length})`;
    else if (!mainEntities[0].name) return 'N/A';

    return `${mainEntities[0].name}${mainEntities[0].isDeleted ? ' (deleted)' : ''}`;
};

const calcAdConnectStatusToDisplay = (isProcessing, updateTime) => {
    let status = isProcessing ? AD_CONNECT_STATUSES.PROCESSING : AD_CONNECT_STATUSES.ACTIVE;
    if (updateTime < Date.now() - 10 * 60 * 1000) {
        status = AD_CONNECT_STATUSES.UNREACHABLE;
    }
    return status;
};

const getFieldNamesFromAccountMatchingStrategy = (accountMatchingStrategy) => {
    if (!accountMatchingStrategy) {
        return [];
    }

    const { type, config } = accountMatchingStrategy;

    if (type === ACCOUNT_MATCHING_TYPES.AD_FIELDS) {
        const userIdField = config?.userId;
        const primaryField = config?.isPrimary?.field;

        if (!userIdField || !primaryField) {
            return [];
        }

        return [userIdField, primaryField];
    }

    return [];
};

const parseComponentVersion = (version) => {
    if (version && VERSION_REGEX.test(version)) {
        const versionSplit = version.split('.');
        const extractVersion = parseInt(versionSplit[2]);
        const major = Math.floor(extractVersion / 1000);
        const minor = extractVersion % 1000;
        return `${versionSplit[0]}.${versionSplit[1]}.${major}.${minor}`;
    }
    return version;
};

const isNewerVersion = (version1, version2) => {
    let isNewer = false;

    const realVersion1 = version1.match(VERSION_REGEX);
    if (realVersion1) {
        const realVersion2 = version2.match(VERSION_REGEX);
        if (realVersion2) {
            const compareVersions = compareVersionsModule.compareVersions || compareVersionsModule;
            isNewer = compareVersions(realVersion1[0], realVersion2[0]) === 1;
        }
    }

    return isNewer;
};
const formatExtraDetailsData = (entity, moreDetailsObject, forExport = false) => {
    const { moreDetailsFormat, moreDetailsHeader, moreDetailsKey } = moreDetailsObject;
    let stringifyDetails = '';

    if (
        entity &&
        entity?.preprocessedExtraDetailsStrings &&
        entity.preprocessedExtraDetailsStrings[moreDetailsHeader]
    ) {
        stringifyDetails = entity.preprocessedExtraDetailsStrings[moreDetailsHeader];
    } else if (
        (entity.extra_data && entity.extra_data[moreDetailsKey] !== undefined) ||
        entity[moreDetailsKey] !== undefined
    ) {
        const details =
            entity.extra_data && entity.extra_data[moreDetailsKey]
                ? entity.extra_data[moreDetailsKey]
                : entity[moreDetailsKey];

        switch (moreDetailsFormat) {
            case 'date':
                stringifyDetails =
                    details > 0
                        ? moment(details * 1000)
                              .local()
                              .format('L')
                        : 'Never';
                break;
            case 'list':
                stringifyDetails = details.join('; ');
                break;

            default:
                stringifyDetails = details;
                break;
        }
    }
    return forExport && stringifyDetails.includes(',') ? `"${stringifyDetails}"` : stringifyDetails;
};

const areArraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) {
        return false;
    }

    const sortedArr1 = [...arr1].map((value) => (typeof value === 'string' ? value.trim() : value)).sort();
    const sortedArr2 = [...arr2].map((value) => (typeof value === 'string' ? value.trim() : value)).sort();

    return sortedArr1.every((value, index) => value === sortedArr2[index]);
};

/**
 * Be careful - this function does not work with numbers
 * @param {object | boolean | string | Array} object The object to check
 * @returns true if the passed object is null, undefined, false, or has no enumerable properties
 */
const isEmpty = (object) => {
    return !object || !Object.keys(object).length;
};

/**
 * Compares two values for deep equality, including nested objects and arrays.
 *
 * @param {*} value1 - The first value to compare.
 * @param {*} value2 - The second value to compare.
 * @returns {boolean} True if the values are deeply equal, false otherwise.
 */
const isEqual = (value1, value2) => {
    if (value1 === value2) return true;

    if (value1 === null || value2 === null || typeof value1 !== 'object' || typeof value2 !== 'object') {
        return false;
    }

    if (Array.isArray(value1) !== Array.isArray(value2)) return false;

    const keys1 = Object.keys(value1);
    const keys2 = Object.keys(value2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
        if (!keys2.includes(key) || !isEqual(value1[key], value2[key])) return false;
    }

    return true;
};

/**
 * Creates a deep copy of an object
 * @param {Object|Array} obj - The object or array to clone.
 * @returns {Object|Array} A deep copy of the input object or array.
 * @note Functions, undefined, symbols, circular references and some unserialized objects (e.g., Date, Map, Set, etc.) are not supported.
 */
const deepClone = (obj) => {
    return JSON.parse(JSON.stringify(obj));
};

const roundDiffToShow = (days, descSortedSteps) => {
    for (let i = 1; i < descSortedSteps.length; i++) {
        if (days > descSortedSteps[i]) return descSortedSteps[i - 1];
    }

    return descSortedSteps[descSortedSteps.length - 1];
};

const baselineTimeChangeToText = (baselineTimeChange) => {
    if (!baselineTimeChange) {
        return 'Not seen';
    }

    const descSortedDaysSteps = BASELINE_CHANGE.sort((a, b) => b - a);
    const maxDiffToShow = descSortedDaysSteps[0];
    const diffInDays = formatDistanceToNowStrict(new Date(baselineTimeChange), { unit: 'day' });
    const diffDaysCount = diffInDays.split(' ')[0];

    if (diffDaysCount > maxDiffToShow) {
        return `Over ${maxDiffToShow} days`;
    }

    return `Last ${roundDiffToShow(diffDaysCount, descSortedDaysSteps)} days`;
};

const buildMonthDaysMap = () => {
    const daysMap = {};
    const firstSuffix = [1, 21, 31];
    const secondSuffix = [2, 22];
    const thirdSuffix = [3, 23];

    for (let i = 1; i <= 31; i++) {
        let suffix = 'th';

        if (firstSuffix.includes(i)) {
            suffix = 'st';
        } else if (secondSuffix.includes(i)) {
            suffix = 'nd';
        } else if (thirdSuffix.includes(i)) {
            suffix = 'rd';
        }

        daysMap[i] = { value: i, label: i + suffix };
    }

    return daysMap;
};

const getRuleIdWithPrefix = (id, ruleType) => {
    if (!Object.values(RULES_TYPES).includes(ruleType)) {
        throw Error('Rule type not valid');
    }
    return `${ruleType}:${id}`;
};

const getRuleTypeAndId = (prefixedRuleId) => {
    const ruleParts = prefixedRuleId.split(':');
    if (ruleParts.length !== 2) {
        throw Error('Rule ID not valid');
    }
    return { type: ruleParts[0], id: ruleParts[1] };
};

const toCamelFromSnakeCase = (fieldName) =>
    fieldName.toLowerCase().replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));

const convertToCamelCaseObject = (value) => {
    const isValueAnObject = typeof value === 'object' && !Array.isArray(value) && value !== null;
    if (!isValueAnObject) {
        return value;
    }

    const result = {};
    for (const key in value) {
        const camelCaseKey = toCamelFromSnakeCase(key);
        result[camelCaseKey] = convertToCamelCaseObject(value[key]);
    }

    return result;
};

const capitalizeFirstLetter = (text) => {
    if (!text || typeof text !== 'string') {
        return text;
    }
    return text[0].toUpperCase() + text.slice(1).toLowerCase();
};

const unsetAndPrune = (obj, path) => {
    // remove the target path
    unset(obj, path);

    // walk up and remove empty parents
    const parts = path.split('.');

    while (parts.length) {
        const currentPath = parts.join('.');
        const val = get(obj, currentPath);

        if (isPlainObject(val) && Object.keys(val).length === 0) {
            unset(obj, currentPath);
        }
        parts.pop(); // move up one level
    }
};

const convertFieldsToDotAnnotation = (obj) => {
    const flatten = (currentObject, parentKey) =>
        transform(
            currentObject,
            (result, value, key) => {
                const newKey = parentKey ? `${parentKey}.${key}` : key;

                if (isPlainObject(value)) {
                    Object.assign(result, flatten(value, newKey));
                } else {
                    result[newKey] = value;
                }
            },
            {}
        );

    return flatten(obj, '');
};

module.exports = {
    splitAndTransformEntities,
    validateAdminConsolePass,
    unixToDate,
    extractDataFromCert,
    riskLevelToText,
    normalizeRisk,
    formatRuleName,
    formatUsername,
    formatUserIdentifier,
    formatResult,
    formatMFATime,
    formatAuthenticator,
    formatDestination,
    formatDate,
    formatService,
    getWasItYouDisplayNameByLog,
    getExtraRiskIndicatorDescription,
    getLocationText,
    shortenTokenID,
    formatUserResponse,
    setLogger,
    getLogsForExport,
    getFormattedLog,
    formatRuleAction,
    isFeatureActive,
    isFeatureDisabled,
    isFeaturePassive,
    isRulesSyncedInNode,
    isEmptyObject,
    getEnumPropByValue,
    getEnumValueByLabel,
    getEnumLabelByValue,
    getEnumEntryByValue,
    getEnumEntryByPropValue,
    esBulkWithErrorThrowing,
    getMainEntityDisplay,
    getGroupByLogsForExport,
    toFirstLetterUppercase,
    calcAdConnectStatusToDisplay,
    getFieldNamesFromAccountMatchingStrategy,
    parseComponentVersion,
    isNewerVersion,
    areArraysEqual,
    formatExtraDetailsData,
    isEmpty,
    isEqual,
    deepClone,
    baselineTimeChangeToText,
    buildMonthDaysMap,
    getRuleIdWithPrefix,
    getRuleTypeAndId,
    convertToCamelCaseObject,
    capitalizeFirstLetter,
    unsetAndPrune,
    convertFieldsToDotAnnotation
};
